import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Button, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { AdminGlobalTable } from "../../../tables/AdminGlobalTable";
import InviteUserToolbarAndModals from "./InviteUserToolbarAndModals";

import { useInviteUserTableData } from "./hooks/useInviteUserTableData";
import { findChild, flattenHierarchy } from "../../../../stores/jotai/base";

import DeleteOutlinedIcon from "../../../../../icons/DeleteOutlinedIcon";
import { RoleMenuSelectItem } from "./menu/RoleMenuSelectItem";
import { GroupMenuItem } from "./menu/GroupMenuSelectItem";
import { CreateSessionData } from "./types";
import { useCreateSession } from "../../../../tanstackQuery/queries/superAdmin/session";
import { useKeycloak } from "@react-keycloak/web";
import { ArrowBack } from "@mui/icons-material";
import { toast } from "sonner";
import { InvoiceMenuItem } from "./menu/InvoiceMenuSelectItem";

export const InviteUserTable = ({
  showInviteTable,
  setShowInviteTable,
  selectedRowData,
  setSelectedRowData,
}) => {
  const [activeModal, setActiveModal] = useState<
    null | "inviteUser" | "assignLicense" | "bulkImport" | "addRole" | "startDate"
  >(null);
  const [userValidationData,setUserValidationData] = useState({})
  const client = useQueryClient();
  const theme = useTheme();
  const { orgID } = useParams();
  const {keycloak} = useKeycloak();

  const {
    inviteList,
    setInviteList,
    rowSelection,
    setRowSelection,
    selectedInviteUserData,
    removeUser,
    getDebouncedUpdater,
    addNewInviteRow,
  } = useInviteUserTableData();

  const invoicesData = client.getQueryData(["get-invoices", { orgID }]) as any[];
  const orgTreeData = client.getQueryData(["orgtree", { orgID }]) as any;
  const ChildData = orgTreeData?.hierarchy
    ? findChild(selectedRowData?.groupID, orgTreeData?.hierarchy)
    : null;
  const cohortTeamList = ChildData ? flattenHierarchy([ChildData]) : null;

  useEffect(() => {
    if (!selectedRowData) return;

    try {
      const parsed = JSON.parse(selectedRowData?.payload);
      if (parsed) {
        setInviteList(parsed);
      }
    } catch (err) {
      toast.info("Invalid JSON, skipped payload sync");
    }
  }, [selectedRowData?.payload]);

  
  const {
    mutate: createSession,
  } = useCreateSession(
    { accessToken: keycloak?.token },
    {
      onSuccess: () => {
        client.invalidateQueries({ queryKey: ["org-invite-sessions"] });
      },
    }
  );

  useEffect(() => {
    if (!inviteList) return;

    const { created, modified, createdF, modifiedF, ...filteredData } = selectedRowData;
    const timeout = setTimeout(() => {
      const payload: CreateSessionData = {
        ...filteredData,
        status: "editing",
        payload: inviteList?.data?.length ? JSON.stringify({
          ...inviteList,
        }) : "{}",
      };
      createSession(payload);
    }, 5000); 
  
    return () => clearTimeout(timeout); 
  }, [inviteList]);

  const columns = useMemo(
    () => [
      {
        header: "First Name",
        accessorKey: "first",
        muiEditTextFieldProps: ({ row }) => ({
          placeholder: "Add first name",
          onChange: (e) => {
            const updater = getDebouncedUpdater(row.original._id, "first");
            updater(e.target.value);
          },
        }),
      },
      {
        header: "Last Name",
        accessorKey: "last",
        muiEditTextFieldProps: ({ row }) => ({
          placeholder: "Add last name",
          onChange: (e) => {
            const updater = getDebouncedUpdater(row.original._id, "last");
            updater(e.target.value);
          },
        }),
      },
      {
        header: "Email",
        accessorKey: "email",
        muiEditTextFieldProps: ({ row }) => ({
          placeholder: "Add email",
          onChange: (e) => {
            const updater = getDebouncedUpdater(row.original._id, "email");
            updater(e.target.value);
          },
        }),
      },
      {
        header: "Group Type",
        enableEditing: false,
        Cell: ({ row }) => (
          <GroupMenuItem
            selectedRowData={inviteList?.data?.find((i) => i?._id === row?.original?._id)}
            cohortTeamList={cohortTeamList}
            setInviteList={setInviteList}
          />
        ),
      },
      {
        header: "Role",
        enableEditing: false,
        Cell: ({ row }) => (
          <RoleMenuSelectItem
            selectedRowData={inviteList?.data?.find((i) => i?._id === row?.original?._id)}
            cohortTeamList={cohortTeamList}
            setInviteList={setInviteList}
          />
        ),
      },
      {
        header: "Invoice",
        enableEditing: false,
        Cell: ({row}) => (
          <InvoiceMenuItem 
            selectedRowData={inviteList?.data?.find((i) => i?._id === row?.original?._id)}
            invoiceList={invoicesData}
            setInviteList={setInviteList}
            inviteList={inviteList}
          />
        ),
      },
      {
        header: "Start Date",
        accessorKey: "startDate",
        muiEditTextFieldProps: () => ({
          type: "date",
          required: true,
          fullWidth: true,
        }),
        enableEditing: false,
      },
      {
        header: "Validation status",
        // accessorKey: "startDate",
        enableEditing: false,
        Cell: ({row}) => {
          const validationStatus = userValidationData ? userValidationData[row?.original?._id] : null;
          return (
            <Tooltip title={validationStatus}>
              <Typography
                sx={{
                  width: "200px",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                }}
              >
                {validationStatus}
              </Typography>
            </Tooltip>
          );
        },
      },
    ],
    [inviteList, cohortTeamList, invoicesData, getDebouncedUpdater,userValidationData]
  );

  const getStructuredRows = (item) =>
    item?.data?.map((value) => ({
      ...value,
      selectedInvoiceSeats: item?.selectedInvoiceSeats,
      startDate: item?.startDate,
    }));
  
  return (
    <Box>
      {
       selectedRowData?.status === "sent" ? (
        <Box sx={{ p: 3 }}>
          <Typography variant="body1" fontWeight={500} color="text.secondary" mb={2}>
            This invitation has already completed.
          </Typography>
          <Button
            variant="contained"
            startIcon={<ArrowBack />}
            onClick={() => {
              setSelectedRowData(null);
              setShowInviteTable(false);
            }}
          >
            Back to Session List
          </Button>
        </Box>
       ) : (
        <AdminGlobalTable
          columns={columns}
          data={getStructuredRows(inviteList)}
          config={{
            enablePagination: false,
            enableBottomToolbar: false,
            enableRowVirtualization: false,
            enableColumnVirtualization: false,
            enableSelectAll: true,
            enableRowSelection: true,
            muiSelectCheckboxProps: { 
              color: "secondary",
              sx:{ 
                '& input[type="checkbox"]': {
                height: '44px !important',
              },}
             },
            createDisplayMode: "row",
            editDisplayMode: "row",
            enableEditing: true,
            positionActionsColumn: "last",
            getRowId: (row) => row._id,
            onRowSelectionChange: setRowSelection,
            state: { rowSelection },
            displayColumnDefOptions: {
              "mrt-row-actions": {
                header: "",
                muiTableHeadCellProps: {
                  sx: { textAlign: "right" },
                },
                muiTableBodyCellProps: {
                  sx: {
                    "& .MuiIconButton-root": {
                      margin: 0,
                      padding: 0,
                    },
                  },
                },
              },
            },
            muiTableBodyCellProps: ({ cell, column, table }) => ({
              onClick: () => {
                table.setEditingCell(cell);
                queueMicrotask(() => {
                  const textField = table.refs.editInputRefs.current?.[column.id];
                  if (textField) {
                    textField.focus();
                    textField.select?.();
                  }
                });
              },
            }),
            muiTableContainerProps: {
              sx: {
                maxHeight: "calc(100vh - 540px)",
                minHeight: "calc(100vh - 540px)",
              },
            },
            muiTableBodyRowProps: () => ({
              hover: false,
              sx: {
                backgroundColor: "transparent",
                ":hover": { backgroundColor: theme.custom.hoverOptionColor },
              },
            }),
            
            renderTopToolbarCustomActions: () => (
              <InviteUserToolbarAndModals
                activeModal={activeModal}
                setActiveModal={setActiveModal}
                inviteList={inviteList}
                setInviteList={setInviteList}
                selectedInviteUserData={selectedInviteUserData}
                cohortTeamList={cohortTeamList}
                invoicesData={invoicesData}
                showInviteTable={showInviteTable}
                setShowInviteTable={setShowInviteTable}
                setSelectedRowData={setSelectedRowData}
                addNewInviteRow={addNewInviteRow}
                selectedRowData={selectedRowData}
                userValidationData={userValidationData}
                setUserValidationData={setUserValidationData}
              />
            ),
            renderRowActions: ({ row }) => (
              <Tooltip title="Remove user">
                <IconButton onClick={() => removeUser(row.original._id)}>
                  <DeleteOutlinedIcon />
                </IconButton>
              </Tooltip>
            ),
          }}
        />
       )
     }
    </Box>
  );
};

export default InviteUserTable;
